import { v4 as uuid } from "uuid"

import getUpdatedMatrix from "reducers/lockingSystemReducer/utils/getUpdatedMatrix"
import getLockingSystemPricePortions, {
	getLockingSystemMsrpPricePortions,
} from "reducers/lockingSystemReducer/utils/getLockingSystemPricePortions"

import {
	DispatchLockingSystemReducer,
	LockingSystemReducerState,
} from "reducers/lockingSystemReducer"
import { LockingSystemComponent } from "@secureo/common/typings/LockingSystem"

type AddLockingComponentPayload = Pick<
	LockingSystemReducerState,
	| "lockingComponentsUuids"
	| "componentsByUuid"
	| "matrix"
	| "matrixType"

	// Price
	| "singleNetPriceCents"
	| "singleGrossPriceCents"
	| "totalNetPriceCents"
	| "totalGrossPriceCents"

	// Msrp price
	| "singleNetMsrpPriceCents"
	| "singleGrossMsrpPriceCents"
	| "totalNetMsrpPriceCents"
	| "totalGrossMsrpPriceCents"
>

export const _getAddDefaultLockingComponentPayload = (
	reducerState: LockingSystemReducerState,
	count: number,
) => {
	const { defaultLockingComponent, keyComponentsUuids, quantity } = reducerState

	const newLockingComponents: LockingSystemComponent[] = []

	for (let i = 0; i < count; i++) {
		const lockingComponent: LockingSystemComponent = {
			...defaultLockingComponent,
			uuid: uuid(),
		}

		newLockingComponents.push(lockingComponent)
	}

	const componentsByUuid = newLockingComponents.reduce((componentsByUuid, lockingComponent) => {
		return {
			...componentsByUuid,
			[lockingComponent.uuid]: lockingComponent,
		}
	}, reducerState.componentsByUuid)

	// Recalculate locking system state
	const lockingComponentsUuids = [
		...reducerState.lockingComponentsUuids,
		...newLockingComponents.map(({ uuid }) => uuid),
	]

	const { matrix, matrixType } = getUpdatedMatrix(
		lockingComponentsUuids,
		keyComponentsUuids,
		reducerState.matrix,
	)

	const { singleNetPriceCents, singleGrossPriceCents, totalNetPriceCents, totalGrossPriceCents } =
		getLockingSystemPricePortions(componentsByUuid, quantity)

	const {
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
	} = getLockingSystemMsrpPricePortions(componentsByUuid, quantity)

	const payload: AddLockingComponentPayload = {
		lockingComponentsUuids,
		componentsByUuid,
		matrix,
		matrixType,

		// Price
		singleNetPriceCents,
		singleGrossPriceCents,
		totalNetPriceCents,
		totalGrossPriceCents,

		// Msrp price
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
	}

	return payload
}

export const addDefaultLockingComponent = (
	dispatch: DispatchLockingSystemReducer,
	reducerState: LockingSystemReducerState,
	count = 1,
) => {
	const payload = _getAddDefaultLockingComponentPayload(reducerState, count)

	dispatch({
		type: "ADD_LOCK_COMPONENT",
		payload,
	})
}
